import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const mouansSartoux: ChauffeurPage = {
  slug: "mouans-sartoux",
  title: "Chauffeur privé à Mouans-Sartoux – VTC",
  subtitle:
    "Chauffeur privé et VTC à Mouans-Sartoux : transfert aéroport de Nice, Espace de l’Art Concret et château, entre Cannes et Grasse.",
  keywords: [
    "chauffeur privé mouans-sartoux",
    "vtc mouans-sartoux",
    "chauffeur mouans sartoux",
    "transfert mouans-sartoux aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Mouans-Sartoux",
    subtitle:
      "Entre Cannes et Grasse, le village de l’Espace de l’Art Concret et de son château : transferts aéroport de Nice et mises à disposition.",
    keywords: [
      "VTC Mouans-Sartoux",
      "chauffeur entre Cannes et Grasse",
      "transfert aéroport Nice",
      "chauffeur Espace de l’Art Concret",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Mouans-Sartoux",
    badges: ["Entre Cannes & Grasse", "Art Concret", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Mouans-Sartoux",
    items: [
      {
        title: "Entre Cannes et Grasse",
        description:
          "Position charnière sur l’axe Cannes–Grasse : le chauffeur relie la Croisette, la capitale du parfum et l’arrière-pays sans souci de trajet.",
      },
      {
        title: "Espace de l’Art Concret & château",
        description:
          "Dépose devant le château de Mouans et le centre d’art contemporain, avec un chauffeur dédié qui patiente pendant la visite.",
      },
      {
        title: "Aéroport de Nice ~35 min",
        description:
          "Rejoignez l’aéroport de Nice Côte d’Azur en environ 35 minutes par l’A8, avec suivi de vol.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, basés sur la Côte d’Azur, ponctuels et discrets — 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Mouans-Sartoux",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge à Mouans-Sartoux avec suivi de vol, vers et depuis l’aéroport de Nice Côte d’Azur, en environ 35 minutes.",
      },
      {
        title: "Liaison Cannes & Grasse",
        text: "Navettes vers la Croisette et vers Grasse, la capitale du parfum, avec un chauffeur qui anticipe le trafic de l’axe.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée : Espace de l’Art Concret, parfumeries de Grasse, Mougins et villages voisins.",
      },
      {
        title: "Culture & art contemporain",
        text: "Dépose au château de Mouans et à l’Espace de l’Art Concret, attente pendant la visite et reprise sur demande.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Mouans-Sartoux : Nice, Monaco, Saint-Tropez, Italie et stations de ski, en véhicule confortable.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Château de Mouans",
        description: "Château triangulaire abritant l’Espace de l’Art Concret.",
      },
      {
        name: "Espace de l’Art Concret",
        description: "Centre d’art contemporain et donation Albers-Honegger.",
      },
      {
        name: "Village de Mouans-Sartoux",
        description: "Cœur du village, marché et commerces.",
      },
      {
        name: "Grasse",
        description: "Capitale du parfum, à quelques minutes par la route.",
      },
      {
        name: "Cannes / Croisette",
        description: "Vers la mer et le Palais des Festivals.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 35 minutes par l’A8.",
      },
    ],
  },
  generic: {
    heading: "Mouans-Sartoux, l’art contemporain entre Cannes et Grasse",
    subheading:
      "Un village charnière sur l’axe Cannes–Grasse, connu pour son château et l’Espace de l’Art Concret.",
    paragraphs: [
      "Mouans-Sartoux occupe une position charnière entre Cannes et Grasse : son château triangulaire abrite l’Espace de l’Art Concret et sa donation d’art contemporain, repère culturel du secteur. Le chauffeur vous dépose devant le château ou au cœur du village et patiente le temps de la visite, sans contrainte de stationnement.",
      "Le village est aussi un point d’ancrage pratique pour rayonner : la Croisette de Cannes d’un côté, les parfumeries de Grasse de l’autre, Mougins et Valbonne à portée de route. Nos chauffeurs anticipent le trafic de l’axe pour tenir vos horaires.",
      "Pour l’aéroport de Nice Côte d’Azur, comptez environ 35 minutes par l’autoroute A8 selon le trafic ; nos chauffeurs bilingues suivent votre vol et adaptent le départ.",
    ],
    bullets: [
      "Axe Cannes–Grasse maîtrisé",
      "Dépose au château & centre d’art",
      "Suivi de vol & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Mouans-Sartoux",
    steps: [
      "Indiquez votre trajet (village, Cannes, Grasse, aéroport de Nice) et vos horaires.",
      "Nous confirmons le véhicule adapté et le forfait fixe ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’adresse convenue.",
      "Ajustements en direct selon le trafic et votre programme.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Mouans-Sartoux"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Comptez environ 35 minutes par l’autoroute A8 selon le trafic, avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Desservez-vous Cannes et Grasse depuis Mouans-Sartoux ?",
      a: "Oui, le village est sur l’axe Cannes–Grasse : nous assurons les liaisons vers la Croisette et vers les parfumeries de Grasse, avec un chauffeur qui anticipe le trafic.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et pendant les événements cannois ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Peut-on visiter l’Espace de l’Art Concret avec chauffeur ?",
      a: "Oui, nous vous déposons devant le château de Mouans et l’Espace de l’Art Concret, avec attente pendant la visite et reprise sur demande.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur Cannes, tarifs et trajets", href: "/chauffeur/cannes" },
    {
      label: "Mise à disposition avec chauffeur Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Réserver un transfert aéroport",
      href: "/chauffeur/aeroport",
    },
    {
      label: "Voitures à louer à Grasse",
      href: "/location/location-voiture-grasse",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Mouans-Sartoux",
    text: "Transfert aéroport de Nice, liaison Cannes–Grasse ou mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Mouans-Sartoux — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à Mouans-Sartoux : transfert aéroport de Nice en ~35 min, Espace de l’Art Concret, liaison Cannes–Grasse. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/mouans-sartoux",
  },
};

export default mouansSartoux;
